import { v4 as uuidV4 } from 'uuid';

import {
  CohortBuilderDefinitionLegacy,
  FilterLegacy,
  InputFieldLegacy,
} from '@verantos-dev/types-cohorts';

const populateInputField = (inputField: InputFieldLegacy, dirty?: boolean): InputFieldLegacy => {
  return {
    ...inputField,
    id: uuidV4(),
    dirty,
  };
};

const populateInputFields = (
  inputFields: InputFieldLegacy[],
  dirty?: boolean
): InputFieldLegacy[] => {
  return inputFields.map((field) => populateInputField(field, dirty));
};

const populateFilter = (filter: FilterLegacy, dirty?: boolean): FilterLegacy => {
  return {
    ...filter,
    inputFields: populateInputFields(filter.inputFields, dirty),
    id: uuidV4(),
  };
};

export const populateIds = (
  dsl?: CohortBuilderDefinitionLegacy,
  options?: { dirty?: boolean }
): CohortBuilderDefinitionLegacy => {
  const { dirty = false } = options || {};

  if (!dsl) {
    return {
      simpleEvents: [],
      derivedEvents: [],
      indexDate: undefined,
      subjectVariables: [],
      studyPeriodRestrictions: undefined,
      // FEATURE FLAG: Initialize analytical variables fields as empty arrays for consistency
      // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these are always included
      analyticalVariables: [],
      criteria: [],
    };
  }

  return {
    simpleEvents: dsl.simpleEvents.map((event) => ({
      ...event,
      id: uuidV4(),
      dirty,
      conceptFilters: event.conceptFilters.map((filter) => populateFilter(filter, dirty)),
      numericFilters: event.numericFilters.map((filter) => populateFilter(filter, dirty)),
      relatedEntityFilters: event.relatedEntityFilters.map((filter) =>
        populateFilter(filter, dirty)
      ),
      uniquenessFilters: event.uniquenessFilters.map((filter) => populateFilter(filter, dirty)),
    })),
    derivedEvents: dsl.derivedEvents.map((event) => ({
      ...event,
      id: uuidV4(),
      dirty,
      eventFilters: event.eventFilters.map((filter) => ({
        ...filter,
        id: uuidV4(),
        dirty,
        eventName: populateInputField(filter.eventName, dirty),
        eventDateAttribute: populateInputField(filter.eventDateAttribute, dirty),
        position: filter.position ? populateInputField(filter.position, dirty) : undefined,
        isSelected: filter.isSelected ? populateInputField(filter.isSelected, dirty) : undefined,
        index: filter.index ? populateInputField(filter.index, dirty) : undefined,
      })),
      temporalConstraints: event.temporalConstraints.map((filter) => populateFilter(filter, dirty)),
      uniquenessFilters: event.uniquenessFilters.map((filter) => populateFilter(filter, dirty)),
    })),
    subjectVariables: dsl.subjectVariables.map((variable) => ({
      ...variable,
      id: uuidV4(),
      dirty,
      conceptFilters: variable.conceptFilters.map((filter) => populateFilter(filter, dirty)),
      numericFilters: variable.numericFilters.map((filter) => populateFilter(filter, dirty)),
      countFilter: variable.countFilter ? populateFilter(variable.countFilter, dirty) : undefined,
    })),
    indexDate: dsl.indexDate
      ? {
          ...dsl.indexDate,
          id: uuidV4(),
          indexDateEvent: dsl.indexDate.indexDateEvent
            ? {
                eventName: populateInputField(dsl.indexDate.indexDateEvent?.eventName, dirty),
                eventDateAttribute: populateInputField(
                  dsl.indexDate.indexDateEvent?.eventDateAttribute,
                  dirty
                ),
                occurrence: populateInputField(dsl.indexDate.indexDateEvent?.occurrence, dirty),
                position: dsl.indexDate.indexDateEvent?.position
                  ? populateInputField(dsl.indexDate.indexDateEvent?.position, dirty)
                  : undefined,
              }
            : undefined,
        }
      : undefined,
    studyPeriodRestrictions: dsl.studyPeriodRestrictions,
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: dsl.analyticalVariables,
    criteria: dsl.criteria,
  };
};
